import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { Times } from '@styled-icons/fa-solid/Times';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { API_URL } from '../../constants/string-constants';

const ModalWrapper = styled.div`
  width: 90%;
  max-width: 400px;
`;

const ModalContent = styled.div`
  background: var(--clr-bg);
  border-radius: 16px;
  padding: 2rem;
  position: relative;
  border: 1px solid rgba(255, 107, 107, 0.3);
`;

const CloseButton = styled(Times)`
  cursor: pointer;
  position: absolute;
  width: 1.5rem;
  height: 1.5rem;
  top: 1rem;
  right: 1rem;
  color: var(--clr-sec);
  opacity: 0.7;
  transition: all 0.2s ease;

  &:hover {
    color: #ff6b6b;
    opacity: 1;
  }
`;

const Title = styled.h2`
  color: #ff6b6b;
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const WarningText = styled.p`
  color: var(--clr-sec);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
`;

const WarningList = styled.ul`
  color: var(--clr-sec);
  font-size: 0.85rem;
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
  padding-left: 1.25rem;
  opacity: 0.9;

  li {
    margin-bottom: 0.25rem;
  }
`;

const ConfirmText = styled.p`
  color: var(--clr-sec);
  font-size: 0.9rem;
  margin: 0 0 0.5rem 0;

  strong {
    color: #ff6b6b;
  }
`;

const ConfirmInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.5);
  border-radius: 8px;
  color: var(--clr-sec);
  font-size: 1rem;
  box-sizing: border-box;

  &::placeholder {
    color: var(--clr-sec);
    opacity: 0.5;
  }

  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const DeleteButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: #ff6b6b;
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #ff5252;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: transparent;
  border: 1px solid var(--clr-sec);
  border-radius: 8px;
  color: var(--clr-sec);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--clr-sec-rgb), 0.1);
  }
`;

const DeleteAccountModal = ({ visible, onClose }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (visible) {
      setConfirmText('');
    }
  }, [visible]);

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return;

    setIsDeleting(true);
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        toast.error('Please sign in to delete your account');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + 'users/delete-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ confirmation: 'DELETE' }),
      });

      if (response.ok) {
        toast.success('Account deleted successfully');
        await auth.signOut();
        onClose();
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        const result = await response.json();
        toast.error(result.error || 'Failed to delete account');
      }
    } catch (err) {
      console.error('Error deleting account:', err);
      toast.error('Failed to delete account. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={visible}
      onRequestClose={onClose}
      style={{
        overlay: { zIndex: 1001, backgroundColor: 'rgba(0,0,0,0.7)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <CloseButton onClick={onClose} />
          <Title>Delete Account</Title>
          <WarningText>
            This action is permanent and cannot be undone. All your data will be deleted, including:
          </WarningText>
          <WarningList>
            <li>Your profile and account number</li>
            <li>All messages you've sent</li>
            <li>Your friends list and friend requests</li>
            <li>Direct message conversations</li>
            <li>Any moderator privileges</li>
          </WarningList>
          <ConfirmText>
            Type <strong>DELETE</strong> to confirm:
          </ConfirmText>
          <ConfirmInput
            type="text"
            placeholder="Type DELETE to confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
          />
          <ButtonContainer>
            <CancelButton onClick={onClose} type="button">
              Cancel
            </CancelButton>
            <DeleteButton
              onClick={handleDelete}
              disabled={confirmText !== 'DELETE' || isDeleting}
              type="button"
            >
              {isDeleting ? 'Deleting...' : 'Delete Account'}
            </DeleteButton>
          </ButtonContainer>
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default DeleteAccountModal;
